import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { InvoiceCard } from "./components";
import { xeroService } from "@/api/services/xero.service";
import { EmptyState } from "@/components/common";
import styles from "./ClientInvoices.module.css";
function mapXeroToInvoice(dto) {
    const isPaid = dto.status?.toUpperCase() === "PAID";
    return {
        id: dto.id,
        invoiceNumber: dto.invoiceNumber,
        address: null,
        suburb: null,
        isPaid,
        amount: dto.total,
        total: dto.total,
        dueDate: dto.dueDate,
        issuedDate: dto.createdAt,
        contactId: dto.contactId,
        jobId: dto.jobId,
        status: dto.status,
        xeroInvoiceId: dto.xeroInvoiceId,
    };
}
export const ClientInvoices = () => {
    const location = useLocation();
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const invoicesBySection = useMemo(() => {
        const forPayment = invoices.filter((invoice) => !invoice.isPaid);
        const paid = invoices.filter((invoice) => invoice.isPaid);
        return { forPayment, paid };
    }, [invoices]);
    const fetchInvoices = useCallback(async () => {
        const contactId = user?.id;
        if (!contactId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await xeroService.getInvoicesByContact(contactId);
            const data = response.data;
            if (data.success && data.invoices) {
                setInvoices(data.invoices.map(mapXeroToInvoice));
            }
            else {
                setInvoices([]);
            }
        }
        catch (err) {
            setInvoices([]);
            setError("Failed to load invoices. Please try again.");
            console.error("Error fetching invoices:", err);
        }
        finally {
            setLoading(false);
        }
    }, [user?.id]);
    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);
    // Scroll to section when hash is present (e.g. from Client Home click)
    useEffect(() => {
        const hash = location.hash?.slice(1);
        if (!hash)
            return;
        const validIds = ["for-payment", "paid"];
        if (!validIds.includes(hash))
            return;
        const timer = setTimeout(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return () => clearTimeout(timer);
    }, [location.hash]);
    const openPaymentLink = useCallback(async (invoiceId, xeroInvoiceId) => {
        const linkId = xeroInvoiceId ?? invoiceId;
        try {
            const response = await xeroService.getInvoiceLink(linkId);
            const data = response.data;
            if (data.success && data.link) {
                window.open(data.link, "_blank");
            }
            else {
                setError(data.error ?? "Failed to get payment link.");
            }
        }
        catch (err) {
            console.error("Error fetching payment link:", err);
            setError("Failed to get payment link.");
        }
    }, []);
    const handlePayInvoice = useCallback(async (invoiceId) => {
        const invoice = invoices.find((i) => i.id === invoiceId);
        await openPaymentLink(invoiceId, invoice?.xeroInvoiceId ?? invoiceId);
    }, [invoices, openPaymentLink]);
    const forPaymentInvoices = invoicesBySection.forPayment;
    const paidInvoices = invoicesBySection.paid;
    return (_jsx(_Fragment, { children: _jsxs("div", { className: styles.page, children: [error && _jsx("div", { className: styles.error, children: error }), loading ? (_jsx("div", { className: styles.loading, children: "Loading invoices..." })) : (_jsxs("div", { className: styles.sectionsGrid, children: [_jsxs("section", { id: "for-payment", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "For Payment" }) }), forPaymentInvoices.length === 0 ? (_jsx("div", { className: `${styles.emptyState} ${styles.invoicesContainer}`, children: _jsx(EmptyState, { image: "/images/empty-state-action.png", heading: "No invoices pending payment", text: "Invoices will appear here once they are issued after quote approval." }) })) : (_jsx("div", { className: styles.invoicesContainer, children: _jsx("div", { className: styles.invoicesGrid, children: forPaymentInvoices.map((invoice) => (_jsx(InvoiceCard, { invoice: invoice, onPayInvoice: handlePayInvoice }, invoice.id))) }) }))] }), _jsxs("section", { id: "paid", className: styles.section, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { className: styles.sectionTitle, children: "Paid" }) }), paidInvoices.length === 0 ? (_jsx("div", { className: `${styles.emptyState} ${styles.invoicesContainer}`, children: _jsx(EmptyState, { image: "/images/empty-state-invoice.png", heading: "No paid invoices yet", text: "Paid invoices will be listed here for your records." }) })) : (_jsx("div", { className: styles.invoicesContainer, children: _jsx("div", { className: styles.invoicesGrid, children: paidInvoices.map((invoice) => (_jsx(InvoiceCard, { invoice: invoice, onPayInvoice: handlePayInvoice }, invoice.id))) }) }))] })] }))] }) }));
};
